import translate from "./Translate";

export const MINUTES_IN_MILLISECONDS = 60 * 1000;
export const HOURS_IN_MILLISECONDS = 60 * MINUTES_IN_MILLISECONDS;
export const DAYS_IN_MILLISECONDS = 24 * HOURS_IN_MILLISECONDS;
export const WEEKS_IN_MILLISECONDS = 7 * DAYS_IN_MILLISECONDS;

/**
 * Compute a day relative to today and return it as an ISO date string (YYYY-MM-DD)
 * @param days number of days to add (can be negative)
 * @returns ISO date string in the format YYYY-MM-DD
 * @example
 * computeDay() // -> today's date
 * computeDay(7) // -> date in 7 days
 */
export const computeDay = (days: number = 0): string => {
  const target = new Date();
  target.setTime(target.getTime() + days * DAYS_IN_MILLISECONDS);
  return target.toISOString().split("T")[0];
};

/**
 * Says if the date time is in the past
 * @param dt_end Date time
 * @returns if the date time is in the past
 * @example
 * isPast("1970/01/01 12:00:00") // -> true
 * isPast("2970/01/01 12:00:00") // -> false
 */
export const isPast = (dt_end: string): boolean => new Date(dt_end).getTime() < Date.now();

/**
 * Format seconds in minutes string
 * @param seconds seconds count
 * @returns sentence containing the same amount of seconds in format `XXminXX` or `XXs`
 * @example
 * formatSecondsMinutely(90) // -> "1min30"
 * formatSecondsMinutely(60) // -> "1min00"
 * formatSecondsMinutely(30) // -> "30s"
 */
export const formatSecondsMinutely = (seconds: number): string => {
  return `${seconds / 60 >= 1 ? `${~~(seconds / 60)}min` : ""}${("00" + (seconds % 60)).slice(-2)}${
    seconds / 60 < 1 ? "s" : ""
  }`;
};

/**
 * Format minutes in hours string
 * @param minutes minutes count
 * @returns sentence containing the same amount of minutes in format "XXhXX" or "XXmin"
 * @example
 * formatMinutesHourly(90) // -> "1h30"
 * formatMinutesHourly(60) // -> "1h00"
 * formatMinutesHourly(30) // -> "30min"
 */
export const formatMinutesHourly = (minutes: number): string => {
  return `${minutes / 60 >= 1 ? `${~~(minutes / 60)}h` : ""}${("00" + (minutes % 60)).slice(-2)}${
    minutes / 60 < 1 ? "min" : ""
  }`;
};

/**
 * Converts a local time hour to UTC, using the browser's timezone offset.
 * @param localHour local time in hour
 * @param targetDate if needs to force target date for having accurate timezone offset
 * @param forceISOHours if needs to force to get double digit hours for ISO usage
 * @returns corresponding UTC hour
 * @example
 * getLocalHourToUTC("17:00") // -> "16:00" (in UTC+1)
 * getLocalHourToUTC("17:00") // -> "15:00" (in UTC+1, if daylight saving)
 * getLocalHourToUTC("17:00") // -> "14:00" (in UTC+3)
 * getLocalHourToUTC("10:00") // -> "9:00" (in UTC+1)
 * getLocalHourToUTC("10:00", null, true) // -> "09:00" (in UTC+1)
 * getLocalHourToUTC("10:00", "2024-01-30", true) // -> "09:00" (in UTC+1)
 * getLocalHourToUTC("10:00", "2024-08-30", true) // -> "08:00" (in UTC+1, as provided date is inside of daylight saving)
 */
export const getLocalHourToUTC = (localHour: string, targetDate?: string, forceISOHours?: boolean): string => {
  const [hours, minutes] = localHour.split(":"),
    offsetHours = (targetDate ? new Date(targetDate) : new Date()).getTimezoneOffset() / -60;
  return ((forceISOHours ? "00" : "") + `${(+hours - offsetHours + 24) % 24}:${minutes}`).slice(-5);
};

/**
 * Format a date or time difference humanly.
 * If the time difference is less than 1 day, it returns the time elapsed in minutes or hours ago.
 * Otherwise, it returns the date in the format 'dd/mm/yyyy'.
 *
 * @param dateCreation The timestamp representing the creation date.
 * @returns A string representing the formatted date or time difference.
 *
 * @example
 * getAgeHumanly(1707295178000) // -> "07/02/2024"
 * getAgeHumanly(1707295178000) // -> "5 hours ago"
 * getAgeHumanly(1707295178000) // -> "2 minutes ago"
 */
export const getAgeHumanly = (language: string, dateCreation: number, showMinutes = false): string => {
  const millisecondsSinceCreation = Date.now() - dateCreation;

  const minutesSinceCreation = millisecondsSinceCreation / MINUTES_IN_MILLISECONDS;
  const hoursSinceCreation = millisecondsSinceCreation / HOURS_IN_MILLISECONDS;
  const daysSinceCreation = millisecondsSinceCreation / DAYS_IN_MILLISECONDS;

  if (daysSinceCreation >= 1) {
    return formatDateHumanly(language, new Date(dateCreation).toISOString(), showMinutes);
  } else if (hoursSinceCreation >= 1) {
    return translate(language, "TIME.AGO").replace(
      "{{TIME}}",
      `${Math.round(hoursSinceCreation)} ${translate(language, hoursSinceCreation > 1 ? "TIME.HOURS" : "TIME.HOUR")}`
    );
  } else if (Math.round(minutesSinceCreation) === 0) {
    return translate(language, "TIME.JUST_NOW");
  } else {
    return translate(language, "TIME.AGO").replace(
      "{{TIME}}",
      `${Math.round(minutesSinceCreation)} ${translate(
        language,
        minutesSinceCreation > 1 ? "TIME.MINUTES" : "TIME.MINUTE"
      )}`
    );
  }
};

/**
 * Format datetime in date string
 * @param language session's language
 * @param date date string
 * @param includeTime Flag indicating whether to include hour times with timezone information (default: false).
 * @returns sentence containing the date compliant with human view
 * @example
 * formatDateHumanly('en-US', '2023-08-09T17:00:00+00:00') // -> "August 09, 2023"
 * formatDateHumanly('en-US', '2023-08-09T17:00:00+00:00', true) // -> "August 09, 2023 5:00 PM (UTC+2)"
 * formatDateHumanly('fr-FR', '2023-08-09T17:00:00+00:00') // -> "09 août 2023"
 * formatDateHumanly('fr-FR', '2023-08-09T17:00:00+00:00', true) // -> "09 août 2023 17:00 (UTC+2)"
 */
export const formatDateHumanly = (language: string, date: string, includeTime: boolean = false): string => {
  const datetime = new Date(date),
    formattedDate = datetime.toLocaleDateString(language, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

  if (includeTime) {
    const timeZone = (-1 * datetime.getTimezoneOffset()) / 60,
      formattedTime = datetime.toLocaleTimeString(language, {
        timeStyle: "short",
      });
    return `${formattedDate} ${formattedTime} (UTC${timeZone >= 0 ? "+" : ""}${timeZone})`;
  } else {
    return formattedDate;
  }
};

/**
 * Converts date time to timestamp in UTC
 * @param datetime Date time
 * @returns the date time in UTC timestamp
 */
export const getUTCTime = (datetime: string): number =>
  new Date(datetime).getTime() - new Date(datetime).getTimezoneOffset() * MINUTES_IN_MILLISECONDS;

/**
 * Format datetime difference in hours:minutes:seconds string
 * @param language session's language
 * @param dateString2 second date string
 * @returns sentence containing the absolute difference in hours:minutes:seconds format
 * @example
 * formatDateDifference('2023-08-09T17:00:00+00:00', '2023-08-09T17:00:00+00:00') // -> '00:00:00'
 * formatDateDifference('2023-08-09T17:00:00+00:00', '2023-08-09T18:00:00+00:00') // -> '01:00:00'
 * formatDateDifference('2023-08-09T17:00:40+00:00', '2023-08-09T17:00:00+00:00') // -> '00:00:40'
 */
export const formatDateDifference = (dateString1: string, dateString2: string): string => {
  const date1 = new Date(dateString1);
  const date2 = new Date(dateString2);

  if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
    return "00:00:00";
  }

  const timeDifference = Math.abs(date2.getTime() - date1.getTime());
  const hours = Math.floor(timeDifference / HOURS_IN_MILLISECONDS);
  const minutes = Math.floor((timeDifference % HOURS_IN_MILLISECONDS) / MINUTES_IN_MILLISECONDS);
  const seconds = Math.floor((timeDifference % MINUTES_IN_MILLISECONDS) / 1000);
  return `${("00" + hours).slice(-2)}:${("00" + minutes).slice(-2)}:${("00" + seconds).slice(-2)}`;
};

/**
 * Convert date format from yyyy/mm/dd to dd/mm/yyyy (Use for component React Calendar)
 * @param dateStr date string
 * @returns date string in dd/mm/yyyy format
 * @example
 * convertDateFormat("2025/3/27") // -> "27/03/2025"
 */

export function convertDateFormat(dateStr: string) {
  const [year, month, day] = dateStr.split("/").map((num) => parseInt(num, 10));
  const formattedMonth = month < 10 ? `0${month}` : month;
  const formattedDay = day < 10 ? `0${day}` : day;
  return `${formattedDay}/${formattedMonth}/${year}`;
}

/**
 * Format date from yyyy-mm-dd to dd/mm/yyyy (Use for core React DatePicker)
 * @param date date string
 * @returns date string in dd/mm/yyyy format
 * @example
 * formatDatePicker("2025-03-27") // -> "27/03/2025"
 * formatDatePicker("2025-3-27") // -> "27/3/2025"
 */

export const formatDatePickerValue = (date: string): string => {
  return date?.split("-").reverse().join("/");
};

/**
 * Parse and normalize date format
 * Supports: YYYY-MM-DD, YYYY/MM/DD, DD-MM-YYYY, DD/MM/YYYY
 * @param dateStr - Date string in various formats
 * @returns ISO formatted date string (YYYY-MM-DD) or original string if parsing fails
 * @example
 * normalizeDate("2025-08-30") // -> "2025-08-30"
 * normalizeDate("2025/08/30") // -> "2025-08-30"
 * normalizeDate("30-08-2025") // -> "2025-08-30"
 * normalizeDate("30/08/2025") // -> "2025-08-30"
 */
export const normalizeDate = (dateStr: string): string => {
  if (!dateStr) return dateStr;

  const cleaned = dateStr.trim().replace(/['"]/g, "");
  const [p1, p2, p3] = cleaned.split(/[/-]/);

  if (!p3) return cleaned;

  const [y, m, d] = p1.length === 4 ? [p1, p2, p3] : [p3, p2, p1];

  if (y.length !== 4 || !d) return cleaned;

  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

export const getDays = (language: string) => {
  const days = new Array(7).fill(null).map((_, i) => {
    const day = new Intl.DateTimeFormat(language, { weekday: "short" }).format(new Date(1970, 0, i + 4));
    return day.charAt(0).toUpperCase() + day.slice(1).replace(".", "");
  });
  return days;
};

export const getMonths = (language: string) => {
  const months = new Array(12).fill(null).map((_, i) => {
    const month = new Intl.DateTimeFormat(language, { month: "short" }).format(new Date(1970, i));
    return month.charAt(0).toUpperCase() + month.slice(1).replace(".", "");
  });
  return months;
};

export const computeRemainingTime = (
  language: string,
  datetime: string | null | undefined,
  translatePrefixe: string,
  translateSuffixe: string
): string => {
  if (!datetime) return "";

  const remainingDays = ~~((new Date(datetime).getTime() - Date.now()) / DAYS_IN_MILLISECONDS);

  if (remainingDays >= 1) {
    return translate(language, `${translatePrefixe}DAY${remainingDays > 1 ? "S" : ""}${translateSuffixe}`).replace(
      "{time}",
      `${remainingDays}`
    );
  } else {
    const remainingHours = ~~((new Date(datetime).getTime() - Date.now()) / HOURS_IN_MILLISECONDS);
    if (remainingHours >= 1) {
      return translate(language, `${translatePrefixe}HOUR${remainingHours > 1 ? "S" : ""}${translateSuffixe}`).replace(
        "{time}",
        `${remainingHours}`
      );
    } else {
      const remainingMinutes = ~~((new Date(datetime).getTime() - Date.now()) / MINUTES_IN_MILLISECONDS);
      return translate(
        language,
        `${translatePrefixe}MINUTE${remainingMinutes > 1 ? "S" : ""}${translateSuffixe}`
      ).replace("{time}", `${remainingMinutes}`);
    }
  }
};

export const computeElapsedTime = (
  language: string,
  datetime: string | null | undefined,
  translatePrefixe: string,
  translateSuffixe: string
): string => {
  if (!datetime) return "";

  const elapsedMilliseconds = Date.now() - new Date(datetime).getTime();
  const elapsedDays = ~~(elapsedMilliseconds / DAYS_IN_MILLISECONDS);

  if (elapsedDays >= 1) {
    return translate(language, `${translatePrefixe}DAY${elapsedDays > 1 ? "S" : ""}${translateSuffixe}`).replace(
      "{time}",
      `${elapsedDays}`
    );
  } else {
    const elapsedHours = ~~(elapsedMilliseconds / HOURS_IN_MILLISECONDS);
    if (elapsedHours >= 1) {
      return translate(language, `${translatePrefixe}HOUR${elapsedHours > 1 ? "S" : ""}${translateSuffixe}`).replace(
        "{time}",
        `${elapsedHours}`
      );
    } else {
      const elapsedMinutes = ~~(elapsedMilliseconds / MINUTES_IN_MILLISECONDS);
      return translate(
        language,
        `${translatePrefixe}MINUTE${elapsedMinutes > 1 ? "S" : ""}${translateSuffixe}`
      ).replace("{time}", `${elapsedMinutes}`);
    }
  }
};

/**
 * Convert a date string to ISO 8601 format (YYYY-MM-DDTHH:MM)
 * @param dateString - The input date string (any valid ISO format)
 * @param keepTimeZoneDiff - If true, adjusts the time to reflect the local time zone difference
 *                           (i.e., simulates the original local time as if it was in UTC).
 *                           If false (default), the time is returned as-is in UTC.
 * @returns The formatted date string in ISO 8601 format (YYYY-MM-DDTHH:MM)
 * @example
 * formatToISODateTime('2023-08-09T17:00:00+00:00') // -> '2023-08-09 17:00'
 * formatToISODateTime('2023-08-09T17:00:00+00:00', true) // -> '2023-08-09 19:00' (if your local time is UTC+2)
 */
export const formatToISODateTime = (dateString: string, keepTimeZoneDiff: boolean = false) => {
  const date = new Date(dateString);
  if (keepTimeZoneDiff) {
    date.setTime(date.getTime() - date.getTimezoneOffset() * MINUTES_IN_MILLISECONDS);
  }
  return date.toISOString().slice(0, 16).replace("T", " ");
};

/**
 * Calculate the end date based on a start date and a duration in minutes.
 * @param startDate The starting date as a string in a valid date format.
 * @param duration The duration in minutes to add to the start date.
 * @returns The calculated end date as an ISO 8601 string (YYYY-MM-DDTHH:MM:SS.sssZ).
 * @example
 * calculateEndDate('2023-08-09T17:00:00Z', 120) // -> '2023-08-09T19:00:00.000Z'
 */
export const calculateEndDate = (startDate: string, duration: number) => {
  const start = new Date(startDate);
  return new Date(start.getTime() + duration * MINUTES_IN_MILLISECONDS).toISOString();
};

/**
 * Returns the localized names of the days of the week in the session's language.
 * If the `excludeWeekEnd` flag is set to `true`, only weekdays (Monday to Friday) are included.
 *
 * @param language session's language
 * @param excludeWeekEnd Param indicating whether to exclude Saturday and Sunday (default: true).
 * @returns An object containing the translated names of the days of the week.
 * @example
 * daysMapping('en-US')
 * { Monday: "Monday", Tuesday: "Tuesday", Wednesday: "Wednesday",
 *   Thursday: "Thursday", Friday: "Friday" }
 *
 * daysMapping('fr-FR', false)
 * { Monday: "Lundi", Tuesday: "Mardi", Wednesday: "Mercredi",
 *   Thursday: "Jeudi", Friday: "Vendredi",
 *   Saturday: "Samedi", Sunday: "Dimanche" }
 */
type DayKeys = "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday";

export const daysMapping = (language: string, excludeWeekEnd: boolean = true): Record<DayKeys, string | undefined> => {
  const days = {
    Monday: translate(language, "TIME.WEEKDAYS.MONDAY"),
    Tuesday: translate(language, "TIME.WEEKDAYS.TUESDAY"),
    Wednesday: translate(language, "TIME.WEEKDAYS.WEDNESDAY"),
    Thursday: translate(language, "TIME.WEEKDAYS.THURSDAY"),
    Friday: translate(language, "TIME.WEEKDAYS.FRIDAY"),
    Saturday: excludeWeekEnd ? undefined : translate(language, "TIME.WEEKDAYS.SATURDAY"),
    Sunday: excludeWeekEnd ? undefined : translate(language, "TIME.WEEKDAYS.SUNDAY"),
  };
  return days;
};

/**
 * Will convert a hours range into local time
 * @param hoursRange hours range string
 * @param date (optional) origin date (helpful to avoid problem with daylight saving time)
 * @returns hours range string with local timezone
 * @example
 * getLocalHourRange("10:00 - 12:00") // "12:00 - 14:00" if we are in UTC+2
 * getLocalHourRange("07:30 - 09:30", "Fri, 28 Mar 2025 07:30:00 GMT") // "08:30 - 10:30" if we are in UTC+1 (winter)
 * getLocalHourRange("14:00 - 15:00", "Mon, 07 Apr 2025 14:00:00 GMT") // "16:00 - 17:00" if we are in UTC+1 (summer)
 */
export const getLocalHourRange = (hoursRange: string, date?: string) => {
  const offsetHours = (-1 * (date ? new Date(date) : new Date()).getTimezoneOffset()) / 60;

  return hoursRange
    .split(" - ")
    .map((hour) => hour.replace(/(\d+):(\d+)/gm, (_, hours, minutes) => `${(+hours + offsetHours) % 24}:${minutes}`))
    .join(" - ");
};

/**
 * Format a datetime range to "HH:mm - HH:mm" in local time
 * @param dtStart ISO / GMT date string
 * @param dtEnd ISO / GMT date string
 * @returns formatted hour range (ex: "14:00 - 15:00")
 * @example
 * // Browser timezone: UTC+1
 * formatHourRange("2025-03-27T14:00:00Z", "2025-03-27T15:30:00Z");
 * // -> "15:00 - 16:30"
 *
 * @example
 * // Browser timezone: UTC+2 (summer time)
 * formatHourRange("2025-04-01T09:00:00Z", "2025-04-01T10:00:00Z");
 * // -> "11:00 - 12:00"
 *
 * @example
 * // Same day, full hour meeting
 * formatHourRange("2025-04-10T13:00:00+00:00", "2025-04-10T14:00:00+00:00");
 * // -> "15:00 - 16:00" (UTC+2)
 *
 * @example
 * // Daylight Saving Time change (Europe)
 * formatHourRange("2025-03-30T01:30:00Z", "2025-03-30T02:30:00Z");
 * // -> "03:30 - 04:30"
 */
export const formatHourRange = (dtStart: string, dtEnd: string): string => {
  const start = new Date(dtStart).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const end = new Date(dtEnd).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${start} - ${end}`;
};

const TimeService = {
  WEEKS_IN_MILLISECONDS,
  DAYS_IN_MILLISECONDS,
  HOURS_IN_MILLISECONDS,
  MINUTES_IN_MILLISECONDS,
  formatMinutesHourly,
  formatDateHumanly,
  computeRemainingTime,
  getAgeHumanly,
  getDays,
  getMonths,
  getLocalHourRange,
  getLocalHourToUTC,
  formatHourRange,
};

export default TimeService;
